import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Navigate, Link } from "react-router-dom";
import { LayoutDashboard, IndianRupee, FileText, HelpCircle, Inbox, Users, History, UserCog, LogOut, ExternalLink, Loader2, Map } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import Overview from "@/components/admin/Overview";
import RatesManager from "@/components/admin/RatesManager";
import ContentEditor from "@/components/admin/ContentEditor";
import FaqEditor from "@/components/admin/FaqEditor";
import EnquiriesInbox from "@/components/admin/EnquiriesInbox";
import AdminUsers from "@/components/admin/AdminUsers";
import ActivityLog from "@/components/admin/ActivityLog";
import MyAccount from "@/components/admin/MyAccount";
import CityWaitlist from "@/components/admin/CityWaitlist";
import { useNoIndex } from "@/components/admin/ui";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, perm: null, Comp: Overview },
  { key: "rates", label: "Scrap Rates", icon: IndianRupee, perm: "rates", Comp: RatesManager },
  { key: "content", label: "Site Content", icon: FileText, perm: "content", Comp: ContentEditor },
  { key: "faqs", label: "FAQs", icon: HelpCircle, perm: "content", Comp: FaqEditor },
  { key: "enquiries", label: "Enquiries", icon: Inbox, perm: "enquiries", Comp: EnquiriesInbox },
  { key: "waitlist", label: "City Waitlist", icon: Map, perm: "enquiries", Comp: CityWaitlist },
  { key: "users", label: "Admin Users", icon: Users, perm: "users", Comp: AdminUsers },
  { key: "activity", label: "Activity Log", icon: History, perm: "activity", Comp: ActivityLog },
  { key: "account", label: "My Account", icon: UserCog, perm: null, Comp: MyAccount },
];
import { ROLE_LABELS } from "@/components/admin/ui";

function useEnquiryAlerts(enabled, onOpen) {
  const [count, setCount] = useState(0);
  const last = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/overview");
        setCount(data.enquiries_new);
        if (last.current !== null && data.enquiries_total > last.current) {
          const n = data.enquiries_total - last.current;
          toast.success(`${n} new enquir${n > 1 ? "ies" : "y"} received`, { action: { label: "Open", onClick: onOpen } });
          if ("Notification" in window && Notification.permission === "granted") new Notification("Kindsaver · New enquiry", { body: `${n} new message${n > 1 ? "s" : ""} on the website`, icon: "/icon-192.png" });
        }
        last.current = data.enquiries_total;
      } catch (_) { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.title = count ? `(${count}) Kindsaver Admin` : "Kindsaver Admin"; }, [count]);
  return count;
}

export default function Admin() {
  const { admin, logout, can } = useAdminAuth();
  const [tab, setTab] = useState("overview");
  useNoIndex();
  const newCount = useEnquiryAlerts(!!admin && can("enquiries"), () => setTab("enquiries"));

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[var(--leaf)]" /></div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const tabs = TABS.filter((t) => !t.perm || can(t.perm));
  const Active = (tabs.find((t) => t.key === tab) || tabs[0]).Comp;

  return (
    <div className="min-h-screen bg-[var(--paper)] lg:flex" data-testid="admin-page">
      <aside className="lg:w-64 lg:min-h-screen bg-[var(--ink)] text-[#E9E4D6] lg:sticky lg:top-0 lg:h-screen flex flex-col">
        <div className="p-5 flex items-center gap-3 border-b border-white/10">
          <img src="/icon-192.png" alt="" className="w-10 h-10 rounded-full" />
          <div><div className="font-display font-bold text-white">Kindsaver</div><div className="text-[11px] text-[#BFD8B8]">Admin console</div></div>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible flex-1" data-testid="admin-nav">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === key ? "bg-[var(--leaf)] text-white" : "hover:bg-white/10"}`}>
              <Icon size={16} /> {label}
              {key === "enquiries" && newCount > 0 && <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[var(--marigold)] text-[var(--ink)] text-[11px] font-bold flex items-center justify-center" data-testid="admin-new-enquiries-badge">{newCount}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 hidden lg:block">
          <div className="text-sm font-semibold text-white truncate" data-testid="admin-current-name">{admin.name}</div>
          <div className="text-xs text-[#BFD8B8]" data-testid="admin-current-role">{ROLE_LABELS[admin.role]}</div>
          <div className="flex gap-2 mt-3">
            <Link to="/" target="_blank" className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg border border-white/15 hover:bg-white/10" data-testid="admin-view-site"><ExternalLink size={12} /> Site</Link>
            <button onClick={logout} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg border border-white/15 hover:bg-white/10" data-testid="admin-logout-btn"><LogOut size={12} /> Logout</button>
          </div>
        </div>
      </aside>
      <main className="flex-1 p-4 sm:p-8 max-w-6xl">
        <div className="lg:hidden flex justify-end mb-4"><button onClick={logout} className="text-sm inline-flex items-center gap-1.5" data-testid="admin-logout-btn-mobile"><LogOut size={14} /> Logout</button></div>
        <Active onNavigate={setTab} />
      </main>
    </div>
  );
}
